import { PlaceBetRequest } from '@bpos/v2/bet-placement';
import { BetslipNotificationRequest } from '@frontend/sports/types/betslip';
import { BetContextualDetails, SaveBetContextRequest } from '@frontend/sports/types/betslip/request';
import { createAction, props } from '@ngrx/store';

import BetslipActions from '../../base/store/actions';
import { IBetslipState } from '../../base/store/state';
import { IBetslipPickStorage } from '../../core/picks/pick-models';
import { ISlip } from '../bet-generation/models';
import { IBetslipResultSummaryViewModel } from '../result/models/betslip-result-summary.model';
import { IBetPicks, IFailedSlipResult, IPlacedBetslipResult, ISucceededPartialSlipResult, ISucceededSlipResult } from './models';
import { PlaceButtonMode } from './state';

export enum PlaceBetFailedReason {
    BetPlacement,
    TechnicalError,
}

export interface PlaceBetPayload {
    request: PlaceBetRequest;
    requestPicks: IBetPicks;
    trackBetContext: BetContextualDetails[];
    trackBetContextV2?: SaveBetContextRequest;
    slips: ISlip[];
}

export interface PlaceBetPayloadWithBalance extends PlaceBetPayload {
    balanceBeforePlacement: number;
}

export interface IPlaceBetSavedPayload extends Omit<PlaceBetPayloadWithBalance, 'requestPicks'> {
    requestPicks: {
        picks: { [betIndex: number]: IBetslipPickStorage };
        groups: { [betIndex: number]: IBetslipPickStorage };
    };
}

export interface QueryPlaceStatusPayload extends PlaceBetPayload {
    state: IBetslipState;
}

export interface FinishPlaceBetPayload {
    request: PlaceBetRequest;
    requestPicks: IBetPicks;
    placeBetResult: IPlacedBetslipResult;
    state: IBetslipState;
}

export class BetslipPlaceBetActions {
    static readonly ACTION_SCHEMA = `${BetslipActions.ACTION_SCHEMA}/place-bet`;

    private static readonly PLACE_BET = `${BetslipPlaceBetActions.ACTION_SCHEMA}/PLACE_BET`;
    private static readonly QUERY_PLACE_STATUS = `${BetslipPlaceBetActions.ACTION_SCHEMA}/QUERY_PLACE_STATUS`;
    private static readonly STARTED_PLACE_BET = `${BetslipPlaceBetActions.ACTION_SCHEMA}/STARTED_PLACE_BET`;
    private static readonly FINISH_PLACE_BET = `${BetslipPlaceBetActions.ACTION_SCHEMA}/FINISH_PLACE_BET`;
    private static readonly TRACK_BET_PLACEMENT_RESULT = `${BetslipPlaceBetActions.ACTION_SCHEMA}/TRACK_BET_PLACEMENT_RESULT`;
    private static readonly PLACE_BET_SUCCEEDED = `${BetslipPlaceBetActions.ACTION_SCHEMA}/PLACE_BET_SUCCEEDED`;
    private static readonly PLACE_BET_FAILED = `${BetslipPlaceBetActions.ACTION_SCHEMA}/PLACE_BET_FAILED`;
    private static readonly PLACE_BET_FAILED_BETSLIP_RESULT = `${BetslipPlaceBetActions.ACTION_SCHEMA}/PLACE_BET_FAILED_BETSLIP_RESULT`;
    private static readonly DISABLE_DOUBLE_BET_PREVENTION = `${BetslipPlaceBetActions.ACTION_SCHEMA}/DISABLE_DOUBLE_BET_PREVENTION`;
    private static readonly UPDATE_SUMMARY_VIEW_MODEL = `${BetslipPlaceBetActions.ACTION_SCHEMA}/UPDATE_SUMMARY_VIEW_MODEL`;
    private static readonly REQUEST_PLACE_BET = `${BetslipPlaceBetActions.ACTION_SCHEMA}/REQUEST_PLACE_BET`;
    private static readonly SAVE_BET_IN_PROGRESS = `${BetslipPlaceBetActions.ACTION_SCHEMA}/SAVE_BET_IN_PROGRESS`;
    private static readonly QUERY_SAVED_BET_IN_PROGRESS_STATUS = `${BetslipPlaceBetActions.ACTION_SCHEMA}/QUERY_SAVED_BET_IN_PROGRESS_STATUS`;
    private static readonly REMOVE_BET_IN_PROGRESS = `${BetslipPlaceBetActions.ACTION_SCHEMA}/REMOVE_BET_IN_PROGRESS`;

    private static readonly SET_NOTIFICATION = `${BetslipPlaceBetActions.ACTION_SCHEMA}/SET_NOTIFICATION`;
    private static readonly SET_MODE = `${BetslipPlaceBetActions.ACTION_SCHEMA}/SET_MODE`;
    private static readonly VALIDATE_PLACE_BUTTON = `${BetslipPlaceBetActions.ACTION_SCHEMA}/VALIDATE_PLACE_BUTTON`;
    private static readonly SET_AUTHENTICATION = `${BetslipPlaceBetActions.ACTION_SCHEMA}/SET_AUTHENTICATION`;
    private static readonly PRINT_RECEIPT = `${BetslipPlaceBetActions.ACTION_SCHEMA}/PRINT_RECEIPT`;

    /**
     * Request Placing Bet
     */
    static requestPlaceBet = createAction(BetslipPlaceBetActions.REQUEST_PLACE_BET);

    /**
     * Query pending bet status
     */
    static queryPlaceStatus = createAction(
        BetslipPlaceBetActions.QUERY_PLACE_STATUS,
        props<QueryPlaceStatusPayload & { balanceAtPlacement: number }>(),
    );

    /* Before request send */
    static startedPlaceBet = createAction(BetslipPlaceBetActions.STARTED_PLACE_BET);
    /* Finish placing bet request */
    static finishPlaceBet = createAction(BetslipPlaceBetActions.FINISH_PLACE_BET, props<FinishPlaceBetPayload>());
    /* Track bet placement result */
    static trackBetPlacementResult = createAction(BetslipPlaceBetActions.TRACK_BET_PLACEMENT_RESULT, props<FinishPlaceBetPayload>());
    /* Successful bet placement */
    static placeBetSucceeded = createAction(BetslipPlaceBetActions.PLACE_BET_SUCCEEDED, props<{ result: IPlacedBetslipResult }>());
    /* Placed Bet Result Summary View Model */
    static updateSummaryViewModel = createAction(
        BetslipPlaceBetActions.UPDATE_SUMMARY_VIEW_MODEL,
        props<{ summary: IBetslipResultSummaryViewModel }>(),
    );

    /* Place bet */
    static placeBet = createAction(BetslipPlaceBetActions.PLACE_BET);

    /*Save bet in progress */
    static saveBetInProgress = createAction(BetslipPlaceBetActions.SAVE_BET_IN_PROGRESS, props<{ payload: PlaceBetPayloadWithBalance }>());

    static querySavedBetInProgressStatus = createAction(
        BetslipPlaceBetActions.QUERY_SAVED_BET_IN_PROGRESS_STATUS,
        props<{ payload: PlaceBetPayloadWithBalance }>(),
    );

    static removeBetInProgress = createAction(BetslipPlaceBetActions.REMOVE_BET_IN_PROGRESS);

    /* Failed bet placement */
    static placeBetFailed = createAction(BetslipPlaceBetActions.PLACE_BET_FAILED, props<{ reason: PlaceBetFailedReason; errorMessage?: string }>());
    static placeBetFailedBetslipResult = createAction(
        BetslipPlaceBetActions.PLACE_BET_FAILED_BETSLIP_RESULT,
        props<{ slips: IFailedSlipResult[] }>(),
    );

    /* Disable double bet prevention */
    static disableDoubleBetPrevention = createAction(BetslipPlaceBetActions.DISABLE_DOUBLE_BET_PREVENTION);
    /* sets notification for the successful betslips */
    static setNotification = createAction(BetslipPlaceBetActions.SET_NOTIFICATION, props<{ request: BetslipNotificationRequest }>());
    /* sets Mode for place button */
    static setMode = createAction(BetslipPlaceBetActions.SET_MODE, props<{ mode: PlaceButtonMode }>());
    /* validate place button if is active or not to place bet*/
    static validatePlaceButton = createAction(BetslipPlaceBetActions.VALIDATE_PLACE_BUTTON, props<{ isValid: boolean }>());
    /* sets Authentication after loggin */
    static setAuthentication = createAction(BetslipPlaceBetActions.SET_AUTHENTICATION, props<{ isAuthenticated: boolean }>());
    static printReceipt = createAction(
        BetslipPlaceBetActions.PRINT_RECEIPT,
        props<{ succeededSlip: ISucceededSlipResult | ISucceededPartialSlipResult; state: IBetslipState }>(),
    );
}
